import { CompanyPhoneNumber, formatPhoneNumberForDisplay } from '@/config/phoneNumbers';
import { ActionIcon, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { forwardRef, ReactElement } from 'react';

interface CallerIdSelectProps {
  phoneNumbers: CompanyPhoneNumber[];
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

interface ItemProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onCopy'> {
  label: string;
  number: string;
  selected: boolean;
  onCopyClick: (e: React.MouseEvent) => void;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, number, selected, onCopyClick, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500}>
            {formatPhoneNumberForDisplay(number)}
          </Text>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          {selected && <IconCheck size={16} color="var(--mantine-color-blue-6)" />}
          <Tooltip label="Copy number">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onCopyClick}
              onMouseDown={(e) => {
                // Prevent the select from opening/closing
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <IconCopy size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </div>
  )
);

SelectItem.displayName = 'SelectItem';

export function CallerIdSelect({ phoneNumbers, value, onChange, disabled }: CallerIdSelectProps): ReactElement {
  const handleCopy = (phoneNumber: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    navigator.clipboard
      .writeText(phoneNumber)
      .then(() => {
        notifications.show({
          title: 'Copied!',
          message: `Phone number ${formatPhoneNumberForDisplay(phoneNumber)} copied to clipboard`,
          color: 'green',
          autoClose: 2000,
        });
      })
      .catch(() => {
        notifications.show({
          title: 'Error',
          message: 'Failed to copy phone number',
          color: 'red',
        });
      });
  };

  const data = phoneNumbers.map((phone) => ({
    value: phone.id,
    label: `${formatPhoneNumberForDisplay(phone.number)} — ${phone.label}`,
    number: phone.number,
    shortLabel: phone.label,
  }));

  return (
    <Select
      label="Caller ID"
      placeholder="Select a phone number"
      data={data}
      value={value}
      onChange={onChange}
      disabled={disabled}
      searchable
      withAsterisk
      renderOption={({ option, checked }) => {
        const phoneData = data.find((d) => d.value === option.value);
        return (
          <SelectItem
            style={{ width: '100%' }}
            label={phoneData?.shortLabel || option.label}
            number={phoneData?.number || ''}
            selected={checked ?? false}
            onCopyClick={handleCopy(phoneData?.number || '')}
          />
        );
      }}
      comboboxProps={{
        width: 'target',
        position: 'bottom-start',
      }}
      styles={{
        input: {
          fontWeight: 500,
        },
      }}
    />
  );
}
